import { useEffect } from 'react';
import { ackChannel } from '../../lib/data';
import { channelPath, getCurrentChannel, navigate } from '../../lib/nav';
import { toggleDeafen, toggleMute } from '../../lib/voice';
import { channelsOfSpace, isUnread, useStore } from '../../store/store';
import { ui, useUi } from '../../store/ui';

export const SHORTCUTS: { keys: string; action: string }[] = [
  { keys: 'Ctrl K', action: 'Quick switcher: jump to a channel, conversation or space' },
  { keys: 'Alt ↑ / Alt ↓', action: 'Previous or next channel' },
  { keys: 'Alt Shift ↑ / ↓', action: 'Previous or next unread channel' },
  { keys: 'Escape', action: 'Mark the current channel as read, cancel a reply or edit' },
  { keys: '↑ in an empty composer', action: 'Edit your last message' },
  { keys: 'Enter / Shift Enter', action: 'Send / new line' },
  { keys: 'Ctrl B / Ctrl I', action: 'Bold / italic in the composer' },
  { keys: 'Ctrl E', action: 'Open the emoji picker' },
  { keys: 'Ctrl Shift M', action: 'Toggle microphone mute' },
  { keys: 'Ctrl Shift D', action: 'Toggle deafen' },
  { keys: 'Ctrl ,', action: 'Open user settings' },
  { keys: 'Ctrl /', action: 'Show keyboard shortcuts' },
];

function sidebarChannels(): string[] {
  const state = useStore.getState();
  const current = getCurrentChannel();
  const channel = current ? state.channels[current] : undefined;
  if (channel?.spaceId) {
    const list = channelsOfSpace(state, channel.spaceId);
    const cats = list.filter((c) => c.type === 'category');
    const ordered = [...list.filter((c) => c.type !== 'category' && !c.parentId), ...cats.flatMap((cat) => list.filter((c) => c.parentId === cat.id))];
    return ordered.filter((c) => c.type !== 'voice' && c.type !== 'stage').map((c) => c.id);
  }
  return Object.values(state.channels)
    .filter((c) => c.type === 'dm' || c.type === 'group_dm')
    .sort((a, b) => (b.lastMessageId ?? b.createdAt).localeCompare(a.lastMessageId ?? a.createdAt))
    .map((c) => c.id);
}

function go(delta: 1 | -1, unreadOnly: boolean): void {
  const state = useStore.getState();
  const ids = sidebarChannels();
  if (ids.length === 0) return;
  const current = getCurrentChannel();
  let idx = current ? ids.indexOf(current) : -1;
  for (let i = 0; i < ids.length; i++) {
    idx = (idx + delta + ids.length) % ids.length;
    const c = state.channels[ids[idx]!]!;
    if (!unreadOnly || isUnread(state, c)) {
      navigate(channelPath(c));
      return;
    }
  }
}

export function useGlobalShortcuts(): void {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey;
      const inField = e.target instanceof HTMLElement && (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName));
      const dialogOpen = !!useUi.getState().dialog;
      if (mod && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        ui.open({ kind: 'quickSwitcher' });
      } else if (mod && e.key === '/') {
        e.preventDefault();
        ui.open({ kind: 'shortcuts' });
      } else if (mod && e.key === ',') {
        e.preventDefault();
        ui.open({ kind: 'userSettings' });
      } else if (mod && e.shiftKey && e.key.toLowerCase() === 'm') {
        e.preventDefault();
        toggleMute();
      } else if (mod && e.shiftKey && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        toggleDeafen();
      } else if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown') && !dialogOpen) {
        e.preventDefault();
        go(e.key === 'ArrowUp' ? -1 : 1, e.shiftKey);
      } else if (e.key === 'Escape' && !dialogOpen && !inField) {
        const id = getCurrentChannel();
        if (id) ackChannel(useStore.getState().channels[id]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
